import React from 'react';
import {
  Button, Card, DatePicker, Form, Input, InputNumber, Popconfirm, Space, Table, Tag, Tooltip, Row, Col,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined, EditOutlined, UndoOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useConsignmentShipForm } from './hooks/useConsignmentShipForm';

const M208Form: React.FC = () => {
  const f = useConsignmentShipForm();

  // Detail table columns
  const detailColumns = [
    {
      title: '項次', dataIndex: 'fm161_02', width: 60, render: (_: unknown, __: unknown, idx: number) => idx + 1,
    },
    {
      title: '產品代號', dataIndex: 'fm161_11', width: 120,
      render: (val: string, _: unknown, idx: number) => f.isEditing
        ? <Input size="small" value={val} onChange={e => f.updateDetail(idx, 'fm161_11', e.target.value)} />
        : val,
    },
    {
      title: '批號', dataIndex: 'fm161_12', width: 120,
      render: (val: string, _: unknown, idx: number) => f.isEditing
        ? <Input size="small" value={val} onChange={e => f.updateDetail(idx, 'fm161_12', e.target.value)} />
        : val,
    },
    {
      title: '顏色代號', dataIndex: 'fm161_13', width: 100,
      render: (val: string, _: unknown, idx: number) => f.isEditing
        ? <Input size="small" value={val} onChange={e => f.updateDetail(idx, 'fm161_13', e.target.value)} />
        : val,
    },
    {
      title: '交運數量', dataIndex: 'fm161_14', width: 110,
      render: (val: number, _: unknown, idx: number) => f.isEditing
        ? <InputNumber size="small" value={val} style={{ width: '100%' }} onChange={v => f.updateDetail(idx, 'fm161_14', v || 0)} />
        : val?.toLocaleString(),
    },
    {
      title: '交運單價', dataIndex: 'fm161_15', width: 110,
      render: (val: number, _: unknown, idx: number) => f.isEditing
        ? <InputNumber size="small" value={val} step={0.01} style={{ width: '100%' }} onChange={v => f.updateDetail(idx, 'fm161_15', v || 0)} />
        : val?.toFixed(2),
    },
    {
      title: '交運金額', dataIndex: 'fm161_16', width: 110, render: (val: number) => val?.toLocaleString(),
    },
    {
      title: '交運單位', dataIndex: 'fm161_18', width: 80,
      render: (val: string, _: unknown, idx: number) => f.isEditing
        ? <Input size="small" value={val} onChange={e => f.updateDetail(idx, 'fm161_18', e.target.value)} />
        : val,
    },
    {
      title: '庫存交運數量', dataIndex: 'fm161_19', width: 120,
      render: (val: number, _: unknown, idx: number) => f.isEditing
        ? <InputNumber size="small" value={val} style={{ width: '100%' }} onChange={v => f.updateDetail(idx, 'fm161_19', v || 0)} />
        : val?.toLocaleString(),
    },
    {
      title: '庫存單位', dataIndex: 'fm161_20', width: 80,
      render: (val: string, _: unknown, idx: number) => f.isEditing
        ? <Input size="small" value={val} onChange={e => f.updateDetail(idx, 'fm161_20', e.target.value)} />
        : val,
    },
    {
      title: '換算率', dataIndex: 'fm161_17', width: 80,
      render: (val: number, _: unknown, idx: number) => f.isEditing
        ? <InputNumber size="small" value={val} step={0.01} style={{ width: '100%' }} onChange={v => f.updateDetail(idx, 'fm161_17', v || 0)} />
        : val,
    },
    ...(f.isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: unknown, idx: number) => (
        <Tooltip title="刪除"><DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} onClick={() => f.removeDetail(idx)} /></Tooltip>
      ),
    }] : []),
  ];

  // List table columns
  const listColumns = [
    { title: '交運單號', dataIndex: 'fm160_01', width: 120 },
    { title: '交運日期', dataIndex: 'fm160_11', width: 100, render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '客戶代號', dataIndex: 'fm160_12', width: 100 },
    { title: 'S/C NO.', dataIndex: 'fm160_21', width: 100 },
    { title: '原幣未稅', dataIndex: 'fm160_17', width: 110, render: (v: number) => v?.toLocaleString() },
    {
      title: '狀態', dataIndex: 'fm160_80', width: 70,
      render: (v: string) => v === 'P' ? <Tag color="green">已核准</Tag> : <Tag>未核准</Tag>,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* Toolbar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          {f.mode === 'browse' && (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={f.startCreate}>新增</Button>
              <Button icon={<EditOutlined />} onClick={f.startEdit} disabled={!f.currentId || f.isApproved}>修改</Button>
              <Popconfirm title="確認刪除此交運單?" onConfirm={f.deleteRecord} disabled={!f.currentId || f.isApproved}>
                <Button danger icon={<DeleteOutlined />} disabled={!f.currentId || f.isApproved}>刪除</Button>
              </Popconfirm>
            </>
          )}
          {f.isEditing && (
            <>
              <Button type="primary" icon={<SaveOutlined />} onClick={f.save} loading={f.loading}>存檔</Button>
              <Button icon={<UndoOutlined />} onClick={f.cancelEdit}>還原</Button>
            </>
          )}
          <Input.Search
            placeholder="搜尋單號/客戶/S/C"
            allowClear
            onSearch={f.search}
            style={{ width: 250 }}
            prefix={<SearchOutlined />}
            disabled={f.isEditing}
          />
        </Space>
      </Card>

      {/* Master Form */}
      <Card title={`客戶寄庫交運單${f.currentId ? ` - ${f.currentId}` : ''}`} size="small" style={{ marginBottom: 12 }}
        extra={f.isApproved ? <Tag color="green">已核准</Tag> : null}>
        <Form layout="vertical" size="small">
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item label="交運單號">
                <Input value={f.master.fm160_01 || ''} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="交運日期" required>
                <DatePicker
                  value={f.master.fm160_11 ? dayjs(f.master.fm160_11) : null}
                  onChange={d => f.updateMaster('fm160_11', d?.toISOString() || null)}
                  disabled={!f.isEditing} style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="S/C NO." required>
                <Input value={f.master.fm160_21 || ''} onChange={e => f.updateMaster('fm160_21', e.target.value)} disabled={!f.isEditing} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="客戶代號" required>
                <Input value={f.master.fm160_12 || ''} onChange={e => f.updateMaster('fm160_12', e.target.value)} disabled={!f.isEditing} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="幣別" required>
                <Input value={f.master.fm160_13 || ''} onChange={e => f.updateMaster('fm160_13', e.target.value)} disabled={!f.isEditing} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="匯率">
                <InputNumber value={f.master.fm160_14} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item label="業務人員" required>
                <Input value={f.master.fm160_15 || ''} onChange={e => f.updateMaster('fm160_15', e.target.value)} disabled={!f.isEditing} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="部門">
                <Input value={f.master.fm160_16 || ''} onChange={e => f.updateMaster('fm160_16', e.target.value)} disabled={!f.isEditing} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="庫位代號" required>
                <Input value={f.master.fm160_22 || ''} onChange={e => f.updateMaster('fm160_22', e.target.value)} disabled={!f.isEditing} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="原幣未稅總額">
                <InputNumber value={f.master.fm160_17} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="稅額">
                <InputNumber value={f.master.fm160_18} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="台幣未稅總額">
                <InputNumber value={f.master.fm160_19} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item label="入單人員">
                <Input value={f.master.fm160_20 || ''} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="總項數">
                <InputNumber value={f.master.fm160_30} style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="修改人員">
                <Input value={f.master.fm160_91 || ''} disabled />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="修改日期">
                <Input value={f.master.fm160_92 ? dayjs(f.master.fm160_92).format('YYYY-MM-DD') : ''} disabled />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Detail Table */}
      <Card title="明細" size="small" style={{ marginBottom: 12 }}
        extra={f.isEditing ? <Button size="small" icon={<PlusOutlined />} onClick={f.addDetail}>新增明細</Button> : null}>
        <Table
          columns={detailColumns}
          dataSource={f.details}
          rowKey={(_, idx) => String(idx)}
          size="small"
          pagination={false}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Record List */}
      <Card title="交運單清單" size="small">
        <Table
          columns={listColumns}
          dataSource={f.records}
          rowKey="fm160_01"
          size="small"
          loading={f.loading}
          pagination={{
            current: f.page,
            pageSize: f.pageSize,
            total: f.total,
            onChange: p => { f.setPage(p); f.loadList(p); },
            showTotal: t => `共 ${t} 筆`,
            showSizeChanger: false,
          }}
          onRow={rec => ({ onClick: () => !f.isEditing && f.loadRecord(rec.fm160_01) })}
          rowClassName={rec => rec.fm160_01 === f.currentId ? 'ant-table-row-selected' : ''}
          style={{ cursor: f.isEditing ? 'default' : 'pointer' }}
        />
      </Card>
    </div>
  );
};

export default M208Form;
